import React, { createContext, useContext, useRef, useState, ReactNode } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useNotification } from '../Notification';
import { ADD_PRODUCT_IMAGE_MUTATION, PRODUCT_IMAGE_CONNECTION_QUERY } from './queries';
import type { Image } from './types';

interface ImagesContextProps {
    images: Image[];
    setImages: React.Dispatch<React.SetStateAction<Image[]>>;
    prependUploadedImages: (_newImages: Image[]) => void;
    appendToImages: (_newImages: Image[]) => void;
    uploadImage: (_file: File, _productId: string | null) => Promise<Image | void>;
    fetchMoreImages: (_productId: string | null, _selectedImageIds?: string[]) => Promise<void>; // Optional endCursor
    loading: boolean;
    loadingMore: boolean;
    hasNextPage: boolean;

}

const ImagesContext = createContext<ImagesContextProps | undefined>(undefined);

export const ImagesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const notification = useNotification();
    const [images, setImages] = useState<Image[]>([]);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    // Whether the first page has ever come back. `loading` was a `useState`
    // that nothing ever set, so it was false for the entire life of the
    // provider — which meant the library could not tell "still fetching" from
    // "you have no pictures", and showed the empty state to anyone on a slow
    // connection before their first page arrived.
    const loadedOnce = useRef(false);
    const loading = !loadedOnce.current && loadingMore;
    const [hasNextPage, setHasNextPage] = useState<boolean>(true);
    const [endCursor, setEndCursor] = useState<string | null>(null);

    const [addProductImage] = useMutation(ADD_PRODUCT_IMAGE_MUTATION);
    const [fetchProductImageConnection] = useLazyQuery(PRODUCT_IMAGE_CONNECTION_QUERY, {
        fetchPolicy: 'no-cache',
    });

    const prependUploadedImages = (newImages: Image[]) => {
        setImages(prevImages => {
            const existingIds = new Set(prevImages.map(img => img.id));
            const filteredNewImages = newImages.filter(img => !existingIds.has(img.id));
            return [...filteredNewImages, ...prevImages];
        });
    };

    const appendToImages = (newImages: Image[]) => {
        setImages(prevImages => {
            const existingIds = new Set(prevImages.map(img => img.id));
            const filteredNewImages = newImages.filter(img => !existingIds.has(img.id));
            return [...prevImages, ...filteredNewImages];
        });
    };

    const uploadImage = async (file: File, _productId: string | null): Promise<Image | void> => {
        const tempId = `${Date.now()}-${Math.random()}`;
        const localUrl = URL.createObjectURL(file);
        const newImage: Image = {
            id: tempId,
            order: images.length,
            isFeatured: false,
            url: localUrl,
            thumbnailUrl: '',
            loading: true,
        };

        prependUploadedImages([newImage]);

        try {
            const { data } = await addProductImage({
                variables: { image: file },
            });

            if (!data?.addProductImage) {
                throw new Error('addProductImage returned no data');
            }
            const finalImage: Image = {
                ...newImage,
                id: data.addProductImage.id ?? newImage.id,
                url: data.addProductImage.url ?? '',
                thumbnailUrl: data.addProductImage.thumbnailUrl ?? '',
                loading: false,
            };

            setImages(prev => prev.map(img => (img.id === tempId ? { ...finalImage, order: img.order } : img)));

            return finalImage;
        } catch (error) {
            // The half-uploaded tile comes out of the grid, and the failure
            // goes back to whoever asked for the upload. It used to be
            // swallowed here into a toast, so the picker could not say
            // anything about it — the one place the seller was looking.
            setImages(prev => prev.filter(img => img.id !== tempId));
            throw error;
        }
    };

    const fetchMoreImages = async (
        productId: string | null,
        selectedImageIds: string[] = [], // Default to an empty array
    ) => {
        if (loadingMore || !hasNextPage) return;

        setLoadingMore(true);
        try {
            const { data } = await fetchProductImageConnection({
                variables: {
                    productId,
                    selectedImageIds: selectedImageIds.length > 0 ? selectedImageIds : undefined, // Handle empty array
                    after: endCursor || null, // Pass null if endCursor is undefined
                    first: 24,
                },
            });

            const edges = data?.productImageConnection?.edges ?? [];
            const newImages: Image[] = edges.map(({ node }: any, idx: number) => ({
                id: node.id,
                order: idx,
                isFeatured: !!node.isFeatured,
                url: node.image.url,
                thumbnailUrl: node.image.thumbnailUrl,
                loading: false,
            }));

            appendToImages(newImages);
            setEndCursor(data?.productImageConnection?.pageInfo.endCursor ?? null);
            setHasNextPage(!!data?.productImageConnection?.pageInfo.hasNextPage);
        } catch (error) {
            console.error("Error fetching more images:", error);
            notification.error({ message: 'Could not load more images.' });
        } finally {
            loadedOnce.current = true;
            setLoadingMore(false);
        }
    };

    return (
        <ImagesContext.Provider value={{
            images,
            setImages,
            prependUploadedImages,
            appendToImages,
            uploadImage,
            fetchMoreImages,
            loading,
            loadingMore,
            hasNextPage,
        }}>
            {children}
        </ImagesContext.Provider>
    );
};

export const useImages = () => {
    const context = useContext(ImagesContext);
    if (!context) throw new Error('useImages must be used within an ImagesProvider');
    return context;
};
